/**
 * The AI customer report, as the account screen asks for it.
 *
 * On request only: nothing here runs when an account opens. The report comes
 * back already validated by the server, with its figures as placeholders and
 * the facts those placeholders name — the values it was written from, which
 * are what the screen shows, so a figure in the report is always one Timely
 * Payment holds.
 */
import { useEffect, useState } from 'react';
import type { AiReport, Fact, FactMap, ReportBasis } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { apiPost } from '../lib/api';

export interface CustomerReportResponse {
    report: AiReport;
    facts: FactMap;
    basis: ReportBasis;
    currency: string;
    generatedAt: string;
    provider: string;
    providerLabel: string;
    model: string;
    promptVersion: string;
    schemaVersion: string;
}

/** Whether this person may ask for a report here, and whether a key is set. */
export function useAiAvailable(workspaceId: string): boolean | null {
    const [available, setAvailable] = useState<boolean | null>(null);
    useEffect(() => {
        let live = true;
        void supabase().rpc('ai_report_available', { p_workspace: workspaceId })
            .then(({ data, error }) => { if (live) setAvailable(error ? false : Boolean(data)); });
        return () => { live = false; };
    }, [workspaceId]);
    return available;
}

const newRequestId = (): string =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));

/** One press of the button: one request id, so a retry is recognised as one. */
export const requestCustomerReport = (workspaceId: string, customerId: string, signal?: AbortSignal) =>
    apiPost<CustomerReportResponse>(
        '/api/ai/customer-report',
        { workspaceId, customerId, requestId: newRequestId() },
        signal ? { signal } : {},
    );

/* ------------------------------ the disclosure ---------------------------- */

// Per person as well as per company: a browser two people share must show the
// second one what is sent where, however many times the first has read it.
const disclosureKey = (workspaceId: string, userId: string) => `timely.ai-report.disclosed.${workspaceId}.${userId}`;

/**
 * Whether this browser has already shown the disclosure for this company. A
 * convenience only: when storage is unavailable the disclosure simply shows
 * every time, which errs the right way.
 */
export function disclosureSeen(workspaceId: string, userId: string): boolean {
    try {
        return window.localStorage.getItem(disclosureKey(workspaceId, userId)) === '1';
    } catch {
        return false;
    }
}

export function rememberDisclosure(workspaceId: string, userId: string): void {
    try {
        window.localStorage.setItem(disclosureKey(workspaceId, userId), '1');
    } catch {
        // Nothing to do: it will be shown again next time.
    }
}

/* ------------------------------ formatting -------------------------------- */

/** A calendar date as written, never shifted by a timezone. */
function formatCalendarDay(value: string, locale: string): string {
    const at = Date.parse(`${value}T00:00:00Z`);
    if (Number.isNaN(at)) return value;
    try {
        return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(at);
    } catch {
        return value;
    }
}

export function formatFact(
    fact: Fact | undefined,
    format: { money: (n: number) => string; count: (n: number) => string; locale: string },
): string {
    if (!fact) return '—';
    switch (fact.kind) {
        case 'money': return format.money(Number(fact.value));
        case 'count': return format.count(Number(fact.value));
        case 'days': {
            const n = Number(fact.value);
            return `${format.count(n)} ${n === 1 ? 'day' : 'days'}`;
        }
        case 'date': return formatCalendarDay(String(fact.value), format.locale);
        default: return String(fact.value);
    }
}
